import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useLayoutEffect,
  useCallback,
  useMemo,
} from 'react';
import { useCurrentUser } from './CurrentUserContext';

export type BreakdownTabType = 'breakdown' | 'schedule' | 'element-manager';

interface BreakdownTabContextType {
  breakdownId: string | null;
  activeTab: BreakdownTabType;
  setBreakdownId: (breakdownId: string | null) => void;
  setActiveTab: (tab: BreakdownTabType) => void;
  switchTab: (tab: BreakdownTabType) => void;
}

const BreakdownTabContext = createContext<BreakdownTabContextType | undefined>(undefined);

export const useBreakdownTabContext = () => {
  const context = useContext(BreakdownTabContext);
  if (context === undefined) {
    throw new Error('useBreakdownTabContext must be used within a BreakdownTabProvider');
  }
  return context;
};

const LS_BREAKDOWN_ID = 'currentBreakdownId';
const LS_BREAKDOWN_TAB = 'currentBreakdownTab';

export const BreakdownTabProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { serverSyncedData, setServerSyncedField } = useCurrentUser();

  const [breakdownId, setBreakdownIdState] = useState<string | null>(() => {
    try {
      return localStorage.getItem(LS_BREAKDOWN_ID);
    } catch {
      return null;
    }
  });

  const [activeTab, setActiveTabState] = useState<BreakdownTabType>(() => {
    try {
      const saved = localStorage.getItem(LS_BREAKDOWN_TAB);
      if (!saved) return 'breakdown';
      if (saved === 'schedule') return 'schedule';
      if (saved === 'breakdown') return 'breakdown';
      if (saved === 'element-manager') return 'element-manager';
      return 'breakdown';
    } catch {
      return 'breakdown';
    }
  });

  useEffect(() => {
    if (breakdownId) {
      localStorage.setItem(LS_BREAKDOWN_ID, breakdownId);
    } else {
      localStorage.removeItem(LS_BREAKDOWN_ID);
    }
  }, [breakdownId]);

  /**
   * 以服务端行为记忆为准同步当前 breakdownId。
   * 使用 useLayoutEffect，避免子路由（如 BreakdownContainer）在同期 useEffect 里误判为空并 navigate 走。
   */
  useLayoutEffect(() => {
    const fromServer = serverSyncedData.userLastUsedBreakdownID ?? null;
    if (fromServer && fromServer !== breakdownId) {
      setBreakdownIdState(fromServer);
    }
  }, [serverSyncedData.userLastUsedBreakdownID, breakdownId]);

  useEffect(() => {
    localStorage.setItem(LS_BREAKDOWN_TAB, activeTab);
  }, [activeTab]);

  const setBreakdownId = useCallback(
    (id: string | null) => {
      setBreakdownIdState(id);
      if (id) {
        setServerSyncedField('userLastUsedBreakdownID', id);
      }
    },
    [setServerSyncedField],
  );

  const setActiveTab = useCallback((tab: BreakdownTabType) => {
    setActiveTabState(tab);
  }, []);

  const switchTab = useCallback((tab: BreakdownTabType) => {
    setActiveTabState(tab);
  }, []);

  const value: BreakdownTabContextType = useMemo(
    () => ({
      breakdownId,
      activeTab,
      setBreakdownId,
      setActiveTab,
      switchTab,
    }),
    [breakdownId, activeTab, setBreakdownId, setActiveTab, switchTab],
  );

  return <BreakdownTabContext.Provider value={value}>{children}</BreakdownTabContext.Provider>;
};
